import React, { useState, useCallback, useRef } from 'react';
import { Typography, Dropdown, Input, Menu } from '../../../components';
import { DownOutlined, TeamOutlined, SearchOutlined } from '@ant-design/icons';

const { Text } = Typography;

export const SearchableOrgDropdown = ({
    organizations,
    currentOrganizationId,
    currentOrganizationName,
    systemRole,
    userRole,
    userRoles,
    onSelect,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const selectedOrgRef = useRef(null);

    const scrollToSelectedOrg = () => {
        requestAnimationFrame(() => {
            selectedOrgRef.current?.scrollIntoView({ block: 'center' });
        });
    };

    const buildItems = useCallback(
        (filterTerm = '') => {
            let filtered;

            if (!filterTerm) {
                filtered = organizations;
            } else {
                const lowerFilter = filterTerm.toLowerCase();
                const matchedIds = new Set();

                organizations.forEach((org) => {
                    if (!org.name.toLowerCase().includes(lowerFilter)) return;

                    if (!org.is_project) {
                        // Matched a parent org — include it and all its child projects
                        matchedIds.add(org.id);
                        organizations.forEach((child) => {
                            if (child.is_project && child.parent_organization_id === org.id) {
                                matchedIds.add(child.id);
                            }
                        });
                    } else {
                        // Matched a project — include it and its parent org
                        matchedIds.add(org.id);
                        if (org.parent_organization_id) {
                            matchedIds.add(org.parent_organization_id);
                        }
                    }
                });

                filtered = organizations.filter((org) => matchedIds.has(org.id));
            }

            return filtered.map((org) => {
                const isSystemAdminAccessible = org.name === 'RiverMeadow' && systemRole === 'ADMIN';
                const isOtherUserRoleAccessible = org.is_project && userRoles.includes(userRole);
                const isDisabled = !(isSystemAdminAccessible || isOtherUserRoleAccessible);
                const isSelected = org.id === currentOrganizationId;

                return {
                    key: org.id,
                    disabled: isDisabled,
                    label: (
                        <span ref={isSelected ? selectedOrgRef : null}>
                            <Text style={org.is_project ? { paddingLeft: 20 } : undefined}>{org.name}</Text>
                        </span>
                    ),
                };
            });
        },
        [organizations, systemRole, userRole, userRoles, currentOrganizationId],
    );

    const popupRender = useCallback(
        () => (
            <div className="org-search-dropdown">
                <div className="org-search-input-wrapper">
                    <Input
                        autoFocus
                        prefix={<SearchOutlined />}
                        placeholder="Search organizations..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
                <Menu
                    items={buildItems(searchTerm)}
                    selectedKeys={currentOrganizationId ? [currentOrganizationId] : []}
                    onClick={({ key }) => onSelect(key)}
                />
            </div>
        ),
        [searchTerm, buildItems, onSelect, currentOrganizationId],
    );

    return (
        <Dropdown
            classNames={{ root: 'org-menu-dropdown' }}
            popupRender={popupRender}
            trigger={['click']}
            onOpenChange={(open) => {
                if (open) {
                    scrollToSelectedOrg();
                } else {
                    setSearchTerm('');
                }
            }}
        >
            <div className="header-dropdown-item">
                <TeamOutlined className="header-icon" />
                <Text className="header-text">{currentOrganizationName}</Text>
                <DownOutlined className="header-arrow" />
            </div>
        </Dropdown>
    );
};
